import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Company } from '@models/company';
import { DataViewColumn, DataViewRow } from '@models/dataview';
import { TemplateItem } from '@models/template-item';
import { NON_CONSOLIDATABLE_COLUMN_PREP_TYPES, NON_MISSING_PERIOD_COLUMN_PREP_TYPES } from '@utils/constants';
import { StatementBuilderMode } from '@utils/enums';
import {LaunchDarklyService} from "@services/launchdarkly.service";

export interface StatmentBuilderOption {
  title: string;
  subtitle: string;
  mode: StatementBuilderMode;
  disabled: boolean;
}

@Component({
    selector: 'app-statement-builder-flyout',
    templateUrl: './statement-builder-flyout.component.html',
    styleUrls: ['./statement-builder-flyout.component.scss'],
    standalone: false
})
export class StatementBuilderFlyoutComponent implements OnInit {
  @Input() company: Company;
  @Input() embeddedMode = false;
  @Input() columns: DataViewColumn[];
  @Input() rows: DataViewRow[];
  @Input() spreadingTemplateItems: any[];
  @Input() statementBuilderEditMode?: StatementBuilderMode;
  @Input() frameToBeEditedId?: any;
  @Output() closeStatementBuilderFlyout = new EventEmitter<any>();

  mode: StatementBuilderMode;
  statementBuilderHeaderText = '';
  showStatementAnnualization = false;
  
  statementBuilderOptions: StatmentBuilderOption[] = [
    {
      title: 'Consolidate Periods',
      subtitle: 'Consolidate multiple periods into one period',
      mode: StatementBuilderMode.Consolidate,
      disabled: false
    },
    {
      title: 'Calculate A Missing Period',
      subtitle: 'Calculate missing data such as a monthly or quarterly period',
      mode: StatementBuilderMode.CalcMissingPeriod,
      disabled: false
    },
    {
      title: 'Create A Scenario',
      subtitle: 'Create a Proforma statement or Projection from a reference period',
      mode: StatementBuilderMode.GenerateForecast,
      disabled: false
    }
  ]
  columnsForConsolidation: DataViewColumn[] = [];
  columnsForMissingPeriodCalcs: DataViewColumn[] = [];

  constructor(
    private launchDarklyService: LaunchDarklyService,
  ) { }

  ngOnInit(): void {
    this.showStatementAnnualization = this.launchDarklyService.flags['statement-annualization-feature-flag'];
    this.mode = !!this.statementBuilderEditMode ? this.statementBuilderEditMode : StatementBuilderMode.Options;
    this.setStatementBuilderHeaderText();

    if (this.showStatementAnnualization) {
      this.statementBuilderOptions.push(
          {
            title: 'Annualize Period',
            subtitle: 'Create an Annualized statement from a reference period',
            mode: StatementBuilderMode.AnnualizePeriod,
            disabled: false
          });
    }

    // Consolidation allows FORECASTED and ANNUALIZED so Statement Builder projections and
    // Annualize Period statements can be consolidated 
    this.columnsForConsolidation = this.columns.filter(c => !NON_CONSOLIDATABLE_COLUMN_PREP_TYPES.includes(c.preparationType));
    // Missing Period calcs allow FORECASTED so Projection/Proforma statements can be used as inputs
    this.columnsForMissingPeriodCalcs = this.columns.filter(c => !NON_MISSING_PERIOD_COLUMN_PREP_TYPES.includes(c.preparationType));
    this.setDisabledFlagForStatementBuilderOptions();
  }

  close(newStatementCreated=false) {
    this.closeStatementBuilderFlyout.emit(newStatementCreated);
  }

  setDisabledFlagForStatementBuilderOptions() {
    const numColumns = this.columns.length;
    this.statementBuilderOptions.forEach(option => option.disabled = numColumns === 0);
  }

  setStatementBuilderHeaderText() {
    switch(this.mode) {
      case StatementBuilderMode.Consolidate: {
         this.statementBuilderHeaderText = 'Consolidate Periods';
         break;
      }
      case StatementBuilderMode.CalcMissingPeriod: {
         this.statementBuilderHeaderText = 'Calculate a Missing Period';
         break;
      }
      case StatementBuilderMode.GenerateForecast: {
        this.statementBuilderHeaderText = 'Create A Scenario';
        break;
     }
      case StatementBuilderMode.DuplicateForecast: {
        this.statementBuilderHeaderText = 'Duplicate Scenario';
        break;
     }
      case StatementBuilderMode.AnnualizePeriod: {
        this.statementBuilderHeaderText = 'Annualize Statement';
        break;
     }
      default: {
         this.statementBuilderHeaderText = 'Statement Builder'
         break;
      }
   }
  }

  selectStatementBuilderOption(mode) {
    this.mode = mode;
    this.setStatementBuilderHeaderText();
  }
}
